import { useCallback, useEffect, useState } from "react";
import axios from "axios";

export type InteractionLog = Record<string, {
  event: string;
  description?: string[];
}[]>;

export type InteractionHistoryState =
  | { status: "loading" | "error" }
  | { status: "ready" | "empty"; log: InteractionLog };

// An empty date group contains no interactions either.
export function parseInteractionLog(data: unknown): InteractionLog {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Invalid interaction history response");
  }

  const groups = Object.entries(data);
  if (groups.some(([, events]) => !Array.isArray(events))) {
    throw new Error("Invalid interaction history response");
  }

  return Object.fromEntries(groups.filter(([, events]) => events.length > 0));
}

const historyState = (log: InteractionLog): InteractionHistoryState => ({
  status: Object.keys(log).length > 0 ? "ready" : "empty",
  log,
});

export default function useInteractionHistory(
  bundleIds: (string | number)[],
  refreshKey: string,
) {
  const requestKey = JSON.stringify([refreshKey, [...new Set(bundleIds.map(String))].sort()]);
  const [snapshot, setSnapshot] = useState<{
    key: string;
    histories: Record<string, InteractionHistoryState>;
  }>({ key: "", histories: {} });

  useEffect(() => {
    const [, ids] = JSON.parse(requestKey) as [string, string[]];
    const controller = new AbortController();
    let nextIndex = 0;
    setSnapshot({ key: requestKey, histories: {} });

    const remember = (id: string, history: InteractionHistoryState) => {
      if (controller.signal.aborted) return;
      setSnapshot((current) => current.key === requestKey
        ? { ...current, histories: { ...current.histories, [id]: history } }
        : current);
    };

    const checkNext = async () => {
      while (!controller.signal.aborted && nextIndex < ids.length) {
        const id = ids[nextIndex++];
        try {
          const { data } = await axios.get(`/consent_bundle/${id}/interaction_log`, {
            signal: controller.signal,
            timeout: 15000,
          });
          remember(id, historyState(parseInteractionLog(data)));
        } catch (_error) {
          // A failed check is unknown, so keep the action available for retry.
          remember(id, { status: "error" });
        }
      }
    };

    // Check only the current rows and avoid sending a whole page of requests at once.
    for (let worker = 0; worker < Math.min(4, ids.length); worker += 1) {
      void checkNext();
    }

    return () => controller.abort();
  }, [requestKey]);

  const rememberHistory = useCallback((id: string | number, log: InteractionLog) => {
    setSnapshot((current) => current.key === requestKey
      ? { ...current, histories: { ...current.histories, [String(id)]: historyState(log) } }
      : current);
  }, [requestKey]);

  return {
    histories: snapshot.key === requestKey ? snapshot.histories : {},
    rememberHistory,
  };
}
